import {visitParents} from 'unist-util-visit-parents'
import {visit} from 'unist-util-visit'
import {staticResourcesEndpoint} from "../utils/communication";
import Markdown from "react-markdown";
import {getCookie} from "../utils/crypto";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeMathJaxSvg from "rehype-mathjax/svg";
import md5 from "md5";
import remarkFrontmatter from "remark-frontmatter";
import {matter} from "vfile-matter";
import rehypeRaw from "rehype-raw";
import React from "react";
import {Link} from "react-router-dom";

function remarkImageURLConvert() {
    return (tree: any) => {
        visitParents(tree, 'image', function (node, parents) {
            if (node.url.startsWith("local:///")) {
                const parent = parents[parents.length - 1];
                const siblings = parent.children;

                const token = getCookie("token");

                if (!token || token === "") {
                    siblings[siblings.indexOf(node)] = {
                        type: 'paragraph',
                        children: []
                    };
                } else {
                    siblings[siblings.indexOf(node)] = {
                        type: 'image',
                        url: node.url.replace(/local:\/\/\/(.+?)\//, staticResourcesEndpoint + "/static/hfsitedata/$1hf/") + "?token=" + token,
                        title: node.title,
                        alt: node.alt
                    };
                }
            }
        })
    }
}

function remarkDocLink() {
    return (tree: any) => {
        visit(tree, 'link', function (node) {
            if (node.url.startsWith("note:///")) {
                node.url = node.url.replace("note:///", "/note/");
            }
        })
    }
}

function remarkPrevNextLink() {
    return (tree: any, file: any) => {
        matter(file);

        if (file.data && file.data.matter) {
            let html = "";

            if (file.data.matter.prev) {
                html += `<a class="prev" href="/note/${file.data.matter.prev}">Prev Note</a>`;
            }

            if (file.data.matter.next) {
                html += `<a class="next" href="/note/${file.data.matter.next}">Next Note</a>`;
            }

            tree.children.push({
                type: "html",
                value: `<div class="prev-next">${html}</div>`
            });
        }
    }
}

export interface TOCItem {
    name: string;
    level: number;
    id: string;
}

function remarkTOC(onTOCGenerated: (items: TOCItem[]) => void) {
    return function (tree: any) {
        let items: TOCItem[] = [];

        visit(tree, 'heading', (node) => {
            const name = node.children.map((child: { value: any; }) => child.value).join(""),
                id = md5(name);

            items.push({
                name: name,
                level: node.depth,
                id: id
            });

            if (!node.data) {
                node.data = {};
            }
            if (!node.data.hProperties) {
                node.data.hProperties = {};
            }

            node.data.hProperties.id = id;
        });

        onTOCGenerated(items);
    }
}

function rehypeAddCaption() {
    function buildFigure({properties: {src, alt}}: { properties: { src: string, alt: string } }) {
        return {
            type: "element",
            tagName: "figure",
            properties: {},
            children: [
                {
                    type: "element",
                    tagName: "img",
                    properties: {src: src, alt: alt},
                    children: []
                },
                {
                    type: "element",
                    tagName: "figcaption",
                    properties: {},
                    children: [{type: "text", value: alt}]
                }
            ]
        };
    }

    return function (tree: any) {
        visit(tree, {tagName: "p"}, (node) => {
            const images = node.children
                .filter((n: { tagName: string; }) => n.tagName === "img")
                .map((img: { properties: { src: string; alt: string; }; }) => buildFigure(img))

            if (images.length === 0) {
                return
            }

            node.tagName = "div";
            node.children = images
        });
    }
}

function rehypeCSV() {
    return function (tree: any) {
        visit(tree, {tagName: "pre"}, (node) => {
            if (node.children.length !== 1) return;

            let child = node.children[0];
            if (child && child.tagName === "code" &&
                child.properties && child.properties.className && child.properties.className.includes("language-csv")) {
                if (child.children.length !== 1) return;

                node.type = "element";
                node.tagName = "div";

                let value = child.children[0].value, meta = (child.data?.meta ?? "").split(" ");
                let rows = value.split("\n"), tableRows = [];

                const headed = meta.includes("headed"), rowPrefixed = meta.includes("row-prefixed");

                // Headed CSV should have 1+n rows, 1 header row and n data rows
                if (headed && rows.length < 2 || !headed && rows.length < 1) return;

                for (let i = headed ? 1 : 0; i < rows.length; i++) {
                    tableRows.push(({
                        type: "element",
                        tagName: "tr",
                        properties: {},
                        children: rows[i].split(",").map((cell: any) => ({
                            type: "element",
                            tagName: "td",
                            properties: {},
                            children: [{type: "text", value: cell.replaceAll("\\", "\n")}]
                        }))
                    }));
                }

                const tbody = {
                    type: "element",
                    tagName: "tbody",
                    properties: {},
                    children: tableRows
                };

                if (headed) {
                    let cells = rows[0].split(",").map((cell: any) => ({
                        type: "element",
                        tagName: "th",
                        properties: {},
                        children: [{type: "text", value: cell}]
                    }));

                    if (rowPrefixed) {
                        cells.unshift({
                            type: "element",
                            tagName: "th",
                            properties: {},
                            children: [{type: "text", value: ""}]
                        });
                    }

                    node.children = [{
                        type: "element",
                        tagName: "table",
                        properties: {
                            className: "csv-table " + (rowPrefixed ? "row-prefixed" : "")
                        },
                        children: [
                            {
                                type: "element",
                                tagName: "thead",
                                properties: {},
                                children: [{
                                    type: "element",
                                    tagName: "tr",
                                    properties: {},
                                    children: cells
                                }]
                            },
                            tbody
                        ]
                    }];
                } else {
                    node.children = [{
                        type: "element",
                        tagName: "table",
                        properties: {
                            className: "csv-table " + (rowPrefixed ? "row-prefixed" : "")
                        },
                        children: [tbody]
                    }];
                }
            }
        });
    }
}

function rehypeReference() {
    let bookRegExp = RegExp('{"type": "book", "name": "(.+?)", "author": "(.+?)", "publisher": "(.+?)", "date": "(\\d+\\.\\d+)", "isbn": "(\\d{3}-\\d-\\d{4}-\\d{4}-\\d)"}'),
        websiteRegExp = RegExp('{"type": "website", "name": "(.+?)", "url": "(.+?)", "date": "(\\d+\\.\\d+)", "access": "(\\d+\\.\\d+)"}');

    return function (tree: any) {
        visit(tree, {type: "text"}, (node) => {
            const value = node.value;

            let bookMatch = value.match(bookRegExp);
            if (bookMatch) {
                node.type = "element";
                node.tagName = "span";
                node.properties = {
                    className: "reference",
                    "data-type": "book",
                    "data-name": bookMatch[1],
                    "data-author": bookMatch[2],
                    "data-publisher": bookMatch[3],
                    "data-date": bookMatch[4],
                    "data-isbn": bookMatch[5]
                };
                node.children = [{
                    type: "text",
                    value: `${bookMatch[1]} (${bookMatch[2]}, ${bookMatch[3]}, ${bookMatch[4]}, ISBN: ${bookMatch[5]}`
                }];
                return;
            }
            let websiteMatch = value.match(websiteRegExp);
            if (websiteMatch) {
                const url = decodeURIComponent(websiteMatch[2]);

                node.type = "element";
                node.tagName = "span";
                node.properties = {
                    className: "reference",
                    "data-type": "website",
                    "data-name": websiteMatch[1],
                    "data-url": url,
                    "data-date": websiteMatch[3],
                    "data-access": websiteMatch[4]
                };
                node.children = [{
                    type: "text",
                    value: `${websiteMatch[1]} (${url}, ${websiteMatch[3]}, Accessed: ${websiteMatch[4]})`
                }];
                return;
            }
        });
    }
}

enum AlertType {
    quote = "quote",
    note = "note",
    tips = "tips",
    info = "info",
    warning = "warning",
    caution = "caution",
    definition = "definition",
    theorem = "theorem",
    corollary = "corollary",
    prof = "prof"
}

function rehypeBlockquote() {
    const reg = /^\[!(quote|note|tips|info|warning|caution|definition|theorem|corollary|prof)] *(.*)(\n|$)(.*)/i;

    return function (tree: any) {
        visit(tree, {tagName: "blockquote"}, (node, index, parent) => {
            if (node.properties.className !== undefined || !node.children || !node.children.length) {
                return;
            }

            let type: AlertType | null = null, title = '';

            /// find the first paragraph
            let child = node.children[1], content = "";

            if (child.type === "element" && child.tagName === "p") {
                if (child.children && child.children.length && child.children[0].type === "text") {
                    const c = child.children[0], v = c.value;

                    if (v.trim() === "") {
                        return;
                    }

                    const match = v.match(reg);
                    if (match) {
                        type = match[1].toLowerCase();
                        title = type!.toUpperCase() + " " + (match[2] || "");

                        if (match[4] !== "") {
                            content = match[4];
                        }
                    }
                }
            }

            if (type) {
                node.properties.className = `blockquote-${type}`;

                node.children[0] = {
                    type: "element",
                    tagName: "div",
                    children: [
                        {
                            type: "text",
                            value: title
                        }
                    ],
                    properties: {
                        className: "blockquote-title"
                    }
                }
                node.children[1].children[0] = {
                    type: "text",
                    value: content
                }
            }
        });
    }
}

export default function HfMarked({
                                     children,
                                     onTOCGenerated = () => {
                                     }
                                 }: {
    children: string | null | undefined,
    onTOCGenerated?: (items: [TOCItem]) => void
}) {
    return (
        <Markdown
            remarkRehypeOptions={{allowDangerousHtml: true}}
            remarkPlugins={[remarkImageURLConvert, remarkDocLink, remarkFrontmatter, remarkPrevNextLink, [remarkTOC, onTOCGenerated], remarkGfm, remarkMath]}
            rehypePlugins={[rehypeAddCaption, rehypeCSV, rehypeReference, rehypeBlockquote, [rehypeMathJaxSvg, {
                tex: {
                    "packages": ["base", "configmacros", "ams", "color"],
                    "macros": {
                        /* Concrete Mathematics */
                        set: ["#1", 1],
                        R: "\\mathbb{R}",
                        Q: "\\mathbb{Q}",
                        Z: "\\mathbb{Z}",
                        C: "\\mathbb{C}",
                        N: "\\mathbb{N}",

                        /* Linear Algebra */
                        vec: ["\\mathbf{#1}", 1],
                        arc: ["\\overset{\\LARGE\\frown}{#1}", 1],
                        norm: ["||#1||_{#2}", 2, ["", ""]],

                        /* Calculus */
                        sup: ["\\mathrm{sup}(#1)", 1],
                        sub: ["\\mathrm{sub}(#1)", 1],
                        grad: "\\mathrm{grad}\\ ",
                        Grad: "\\nabla",
                        diff: "\\mathrm{d}",
                        bool: ["\\mathrm{bool}(#1)", 1],

                        /* Logic */
                        Larrow: "\\Leftarrow",
                        Rarrow: "\\Rightarrow",
                        LRarrow: "\\Leftrightarrow",
                        larrow: "\\leftarrow",
                        rarrow: "\\rightarrow",
                        lrarrow: "\\leftrightarrow",

                        defeq: "\\overset{\\mathrm{def}}{=}",
                    },
                },
            }], rehypeHighlight, rehypeRaw]}
            components={{
                a(props) {
                    return <Link to={props.href ?? ""}>{props.children}</Link>;
                }
            }}
            className="markdown">
            {children}
        </Markdown>
    )
}